const {coordinate} = require('../../utils/timesheetCellSelection');
const {summarizeSelection} = require('../../utils/timesheetSelectionSummary');
const {number} = require('../../utils/timesheetDaySummary');
const {workRange, orderedRows} = require('../../utils/timesheetSourceOrder');

function sourceRefs(parts) {
    return parts.map(part => [Number(part.revision_id) || 0, Number(part.timesheet_id) || 0,
        Number(part.line_id) || 0, Number(part.building_id) || 0]);
}
function sourceSignature(run, line) {
    return JSON.stringify([Number(run.id), Number(run.calculation_version), Number(line.id),
        sourceRefs(line._allComponents || (line.calculation_details || {}).components || [])]);
}
function visibleRows(rows, parts) {
    return rows.filter(row => parts.some(part => (Number(part.revision_id) || 0) === Number(row.revision_id)
        && (Number(part.timesheet_id) || 0) === Number(row.timesheet_id) && Number(part.building_id || 0) === Number(row.building_id)
        && (!part.line_id || Number(part.line_id) === Number(row.line_id))));
}
function workLabel(row) {
    if (!row.available) return row.unavailable_reason || 'Источник недоступен';
    const {first, last} = workRange(row);
    if (!first) return 'Без рабочих часов';
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    const label = date => `${date.slice(-2)} ${months[Number(date.slice(5, 7)) - 1]}`;
    return `Работа: ${first === last ? label(first) : first.slice(0, 7) === last.slice(0, 7)
        ? `${first.slice(-2)}–${label(last)}` : `${label(first)}–${label(last)}`}`;
}
function entryValue(row, date) {
    const entry = row.entries.find(item => item.work_date === date);
    if (!entry) return {worked_minutes: 0, codes: []};
    const byId = new Map(row.codes.map(code => [Number(code.id), code.code]));
    const minutes = Number(entry.worked_minutes || 0);
    if (!Number.isFinite(minutes) || minutes < 0) throw Error('Некорректные часы');
    const codes = [entry.absence_code_id, entry.travel_code_id, entry.other_code_id].filter(Boolean).map(id => {
        if (!byId.has(Number(id))) throw Error('Неизвестная отметка');
        return byId.get(Number(id));
    });
    return {worked_minutes: minutes, codes};
}
function cellText(row, date) {
    try {
        const value = entryValue(row, date);
        return [value.worked_minutes ? number(value.worked_minutes / 60) : !value.codes.length && row.entries.some(entry => entry.work_date === date) ? '0' : '', ...value.codes].filter(Boolean).join('');
    } catch (error) { return '?'; }
}
function rowCells(row, dates) {
    const entries = new Map((row.entries || []).map(entry => [entry.work_date, entry]));
    const codes = new Map((row.codes || []).map(code => [Number(code.id), code]));
    return new Map(dates.map(date => {
        const entry = entries.get(date), tokens = [];
        if (!entry) return [date, {text: '', tokens, kind: 'empty', hint: ''}];
        if (row.historical && entry.source_value != null) {
            const text = String(entry.source_value);
            return [date, {text, tokens: [{text, kind: 'reference', hint: 'Исходная архивная отметка'}], kind: 'reference', hint: 'Исходная архивная отметка'}];
        }
        const minutes = Number(entry.worked_minutes || 0);
        const fields = ['absence_code_id', 'travel_code_id', 'other_code_id'].filter(field => entry[field]);
        if (!Number.isFinite(minutes) || minutes < 0 || fields.some(field => !codes.has(Number(entry[field])))) {
            return [date, {text: '?', tokens: [{text: '?', kind: 'unknown'}], kind: 'unknown', hint: 'Некорректные часы или неизвестная отметка. Проверьте источник.'}];
        }
        if (minutes > 0) tokens.push({text: number(minutes / 60), kind: 'signal', hint: 'Рабочие часы'});
        fields.forEach(field => {
            const code = codes.get(Number(entry[field]));
            const travel = field === 'travel_code_id';
            const kind = travel && entry.travel_contributes == null ? 'context'
                : travel && entry.travel_contributes ? 'signal' : 'reference';
            const hint = travel ? entry.travel_contributes == null ? 'Вклад дороги в расчёт не определён'
                : entry.travel_contributes ? 'Дорога учтена в исходных расчётных днях' : 'Отметка дороги не добавляет расчётного количества'
                : 'Справочная отметка';
            tokens.push({text: code.code, kind, hint: `${code.name || code.code}: ${hint}`});
        });
        if (!tokens.length) tokens.push({text: '0', kind: 'reference', hint: 'Явно указано ноль часов'});
        return [date, {text: tokens.map(token => token.text).join(''), tokens,
            kind: tokens.some(token => token.kind === 'signal') ? 'signal' : tokens.some(token => token.kind === 'context') ? 'context' : 'reference',
            hint: tokens.map(token => token.hint).join('. ')}];
    }));
}
function selection(rows, keys) {
    const selected = new Set(keys), records = [], invalid = [], codes = [];
    const known = new Set();
    // Source order is the same as in payroll, even if the user selects backwards.
    rows.forEach(row => {
        codes.push(...row.codes);
        selected.forEach(key => {
            const cell = coordinate(key);
            if (cell.lineId !== row.id) return;
            known.add(key);
            try {
                if (!row.available) throw Error('Источник недоступен');
                records.push({...entryValue(row, cell.date), key: `${row.employee_id}:${row.building_id}:${cell.date}`, policy: row.day_counting});
            } catch (error) { invalid.push(key); }
        });
    });
    selected.forEach(key => { if (!known.has(key)) invalid.push(key); });
    return summarizeSelection(records, selected.size, invalid, 0, codes);
}
module.exports = {sourceRefs, sourceSignature, visibleRows, workRange, orderedRows, workLabel, entryValue, cellText, rowCells, selection};
